/**
 * inspector.ts — whether the right-hand panel is collapsed, as pure functions.
 *
 * ANDREI, 2026-08-05: "the panel from the right can be minimized too, right?" It could not, so on
 * a 3-pane grid 300px of width went to text he was not reading while the charts were the thing he
 * was looking at.
 *
 * THE NAME IS NOW HISTORICAL, deliberately. The per-bar "Inspector" section this module was named
 * for was DELETED on 2026-08-18 (card t_677775e0) when Andrei asked again — "the pane from the
 * right brings almost no value" — and the panel kept only its non-diagnostic contents (the overlay
 * summary, the detector selects, the source caveat, provenance). The collapse preference outlived
 * the section, and the file is NOT renamed because `INSPECTOR_COLLAPSED_KEY` is a live
 * localStorage key: renaming the key to match a tidier module name would silently forget the
 * saved state of every user who ever collapsed the panel, which is a regression paid to make a
 * filename read better.
 *
 * WHY A MODULE FOR TWO FUNCTIONS. `main.ts` calls `createChart` and `localStorage` at import time,
 * so vitest cannot import it (see `panelayout.test.ts` for the source-text workaround that costs).
 * The DECISION -- what a stored value means, and what an absent one means -- is exactly the part
 * that must be witnessed by a test rather than by a browser: DEFAULT OPEN is a requirement, and a
 * `=== '1'` written the other way round would silently hide a panel he has been using. The DOM
 * writes stay in `main.ts`.
 *
 * SAME KEY SHAPE AS THE TRADE PANEL'S (`tp.collapsed`, '1'/'0'), because that panel already stores
 * a collapse preference this way and two encodings of one idea is how they drift.
 */

/** localStorage key for the inspector's collapse preference. */
export const INSPECTOR_COLLAPSED_KEY = 'insp.collapsed';

/**
 * Should the inspector start collapsed, given what storage holds?
 *
 * DEFAULT OPEN, and that is the load-bearing branch: `null` (never toggled) and any junk a user or
 * an older build left behind both mean "show it". Andrei has been reading the inspector -- shipping
 * a hidden-by-default panel would remove a feature he uses in the name of a feature he asked for.
 * Only the explicit '1' this app writes collapses it.
 */
export function inspectorStartsCollapsed(stored: string | null): boolean {
  return stored === '1';
}

/** What to store for a given state. The inverse of `inspectorStartsCollapsed`, so a round trip
 *  through storage is identity -- pinned by a test, because a writer that stored 'true' while the
 *  reader tested for '1' would forget the preference on every reload and look like flakiness. */
export function inspectorCollapsedValue(collapsed: boolean): string {
  return collapsed ? '1' : '0';
}
